import React from 'react';
import { action, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';

import { ComponentBase } from '../../../shared/ComponentBase.js';
import { InjectedStyledComponent } from '../../../shared/InjectedStyledComponent.js';
import { IToastProps } from '../Toast/models/index.js';
import Toast from '../Toast/Toast.js';

import { IToasterConfiguration, IToasterProps, IToasterState } from './models/index.js';
import ToastWrapper from './styledComponents/ToastWrapper.js';
import Wrapper from './styledComponents/Wrapper.js';
import { TOASTER_CATEGORY_KEY, TOASTER_COMPONENT_KEY } from './Toaster.meta.js';

export class Toaster extends ComponentBase<IToasterProps, IToasterState, IToasterConfiguration> {
	categoryKey = TOASTER_CATEGORY_KEY;
	componentKey = TOASTER_COMPONENT_KEY;

	timeouts: { [key: string]: any } = {};
	items: IToastProps[] = [];

	constructor(props: IToasterProps) {
		super(props);

		makeObservable(this, {
			items: observable.ref,
			setItems: action,
			handleClose: action,
		});

		if (props.items) {
			this.items = props.items;
			this.setupTimeout();
		}
	}

	componentDidUpdate(previousProps: IToasterProps) {
		const { items: previousItems = [] } = previousProps;
		const { items = [] } = this.props;
		const oldIds = previousItems
			.map((previousItem) => previousItem.toastId)
			.sort()
			.join(',');
		const newIds = items
			.map((item) => item.toastId)
			.sort()
			.join(',');
		if (typeof previousItems !== typeof items || previousItems.length !== items.length || newIds !== oldIds) {
			if (this.props.items) {
				this.setItems(this.props.items);
				this.setupTimeout();
			}
		}
	}

	setItems = (items: IToastProps[]) => {
		this.items = items;
	};

	setupTimeout = () => {
		this.items.forEach((item) => {
			const { toastId, displaySeconds } = item;
			if (displaySeconds && !this.timeouts[toastId]) {
				this.timeouts[toastId] = setTimeout(() => {
					this.handleClose(toastId);
				}, displaySeconds * 1000);
			}
		});
	};

	handleClose = (toastId: string) => {
		this.items = this.items.filter((item) => item.toastId !== toastId);
		const { onToastClose } = this.props;
		if (onToastClose) {
			onToastClose(toastId);
		}
	};

	render() {
		const { className } = this.props;
		return (
			<Wrapper className={className}>
				{this.items.map((item) => {
					const { toastId } = item;
					return (
						<ToastWrapper key={toastId}>
							<Toast {...item} onClose={this.handleClose} />
						</ToastWrapper>
					);
				})}
			</Wrapper>
		);
	}
}

export default InjectedStyledComponent(observer(Toaster), TOASTER_CATEGORY_KEY, TOASTER_COMPONENT_KEY);
